import React, { useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  id: string;
  label: string;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  required?: boolean;
  error?: string;
  disabled?: boolean;
  helpText?: string;
  maxSelections?: number;
  showOtherOption?: boolean;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  label,
  selectedValues,
  onChange,
  options,
  required = false,
  error,
  disabled = false,
  helpText,
  maxSelections,
  showOtherOption = false,
  otherValue = '',
  onOtherChange,
}) => {
  const [showOtherInput, setShowOtherInput] = useState(selectedValues.includes('Other'));

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      if (maxSelections && selectedValues.length >= maxSelections) {
        return;
      }
      const newValues = [...selectedValues, value];
      onChange(newValues);

      if (value === 'Other') {
        setShowOtherInput(true);
      }
    } else {
      const newValues = selectedValues.filter(v => v !== value);
      onChange(newValues);

      if (value === 'Other') {
        setShowOtherInput(false);
        onOtherChange?.('');
      }
    }
  };

  return (
    <div className="mb-4">
      <fieldset disabled={disabled}>
        <legend className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {maxSelections && (
            <span className="text-sm text-gray-500 ml-2">
              (Select up to {maxSelections})
            </span>
          )}
        </legend>
        <div className="space-y-2">
          {options.map((option) => (
            <div key={option.value} className="flex items-start">
              <input
                type="checkbox"
                id={`${id}-${option.value}`}
                checked={selectedValues.includes(option.value)}
                onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                disabled={disabled || option.disabled || Boolean(maxSelections && selectedValues.length >= maxSelections && !selectedValues.includes(option.value))}
                className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2"
              />
              <label
                htmlFor={`${id}-${option.value}`}
                className={`ml-3 block text-sm ${
                  disabled || option.disabled || (maxSelections && selectedValues.length >= maxSelections && !selectedValues.includes(option.value))
                    ? 'text-gray-400'
                    : 'text-gray-700'
                } cursor-pointer`}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>

        {showOtherOption && showOtherInput && (
          <div className="mt-3">
            <label htmlFor={`${id}-other-input`} className="block text-sm font-medium text-gray-700 mb-1">
              Please specify:
            </label>
            <input
              type="text"
              id={`${id}-other-input`}
              value={otherValue}
              onChange={(e) => onOtherChange?.(e.target.value)}
              disabled={disabled}
              placeholder="Enter other condition"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        )}
      </fieldset>

      {helpText && !error && (
        <p className="mt-1 text-sm text-gray-500">
          {helpText}
        </p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default MultiSelect;